// src/lib/articles.js
import supabase from "../supabase";
import { getAuthHeader } from "./authFetch";

/**
 * Non-ai_enabled orgs skip the "Ready for Review" checkpoint and don't see
 * granular in-between states — anywhere their article status is displayed,
 * it should read "In Progress" unless it's actually Ready for Approval or
 * Ready to Publish.
 */
export function getDisplayStatus(rawStatus, aiEnabled) {
  if (aiEnabled) return rawStatus || "In Progress";
  if (rawStatus === "Ready for Approval" || rawStatus === "Ready to Publish") {
    return rawStatus;
  }
  return "In Progress";
}

/** List articles (optionally filter by project) */
/** List articles (optionally filter by project) */
export async function listOrgArticles({ project_id, org_id } = {}) {
  if (!org_id) {
    console.error("listOrgArticles was called without an org_id!");
    return [];
  }

  // 1. Fetch articles where the user is the MAIN ORG (Owner)
  let queryOwner = supabase
    .from("articles")
    .select(
      `id, title, created_at, updated_at, progress, score, created_by, project_id, org_id, client_org_id, Users(name)`
    )
    .eq("org_id", org_id);

  // 2. Fetch articles where the user is the BRAND (Client)
  let queryClient = supabase
    .from("articles")
    .select(
      `id, title, created_at, updated_at, progress, score, created_by, project_id, org_id, client_org_id, Users(name)`
    )
    .eq("client_org_id", org_id);

  // Apply project filters if they exist
  if (project_id === null) {
    queryOwner = queryOwner.is("project_id", null);
    queryClient = queryClient.is("project_id", null);
  } else if (project_id) {
    queryOwner = queryOwner.eq("project_id", project_id);
    queryClient = queryClient.eq("project_id", project_id);
  }

  // Run both queries simultaneously for maximum speed
  const [ownerRes, clientRes] = await Promise.all([queryOwner, queryClient]);

  if (ownerRes.error) throw ownerRes.error;
  if (clientRes.error) throw clientRes.error;

  // Combine the lists and remove any accidental duplicates
  const combined = [...(ownerRes.data || []), ...(clientRes.data || [])];
  const uniqueMap = new Map();
  combined.forEach((art) => uniqueMap.set(art.id, art));
  const uniqueData = Array.from(uniqueMap.values());

  // Sort them so the newest articles are at the top
  uniqueData.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

  // Map the creator name safely
  return uniqueData.map((article) => ({
    ...article,
    creator_name: article.Users?.name || "Unknown",
  }));
}

/** Create new article */
export async function createArticle({
  title,
  content_html,
  meta_tags,
  content_brief,
  project_id,
  org_id,
  client_org_id,
}) {
  const {
    data: { user },
    error: authErr,
  } = await supabase.auth.getUser();
  if (authErr) throw authErr;

  const { data, error } = await supabase
    .from("articles")
    .insert({
      title,
      content_html,
      meta_tags: meta_tags ?? null,
      content_brief: content_brief ?? null,
      created_by: user.id,
      project_id: project_id ?? null,
      org_id, // The Main Org
      client_org_id: client_org_id ?? null,
    })
    .select("id")
    .single();

  if (error) throw error;
  return data.id;
}

/** Get a single article */
export async function getArticle(id) {
  const { data, error } = await supabase
    .from("articles")
    .select(
      `
      id,
      title,
      content_html,
      meta_tags,
      content_brief, 
      progress,
      score,
      readability_score,
      topic_authority_score,
      content_value_score,
      seo_score,
      created_by,
      project_id,
      org_id,
      client_org_id,
      created_at,
      updated_at,
      Users:created_by (
        id,
        name,
        email
      )
    `
    )
    .eq("id", id)
    .single();

  if (error) throw error;

  return {
    ...data,
    creator_name: data.Users?.name || null,
    creator_email: data.Users?.email || null,
  };
}

/** Update existing article (also supports progress) */
export async function updateArticle(
  id,
  {
    title,
    content_html,
    meta_tags,
    content_brief,
    progress,
    project_id,
    score,
    readability_score,
    topic_authority_score,
    content_value_score,
    seo_score,
    client_org_id,
  }
) {
  const { data, error } = await supabase
    .from("articles")
    .update({
      ...(title !== undefined ? { title } : {}),
      ...(content_html !== undefined ? { content_html } : {}),
      ...(meta_tags !== undefined ? { meta_tags } : {}),
      ...(content_brief !== undefined ? { content_brief } : {}),
      ...(progress !== undefined ? { progress } : {}),
      ...(project_id !== undefined ? { project_id } : {}),
      ...(score !== undefined ? { score } : {}),
      ...(readability_score !== undefined ? { readability_score } : {}),
      ...(topic_authority_score !== undefined ? { topic_authority_score } : {}),
      ...(content_value_score !== undefined ? { content_value_score } : {}),
      ...(seo_score !== undefined ? { seo_score } : {}),
      ...(client_org_id !== undefined ? { client_org_id } : {}),
    })
    .eq("id", id)
    .select(
      "id, title, progress, project_id, score, readability_score, topic_authority_score, content_value_score, seo_score, updated_at"
    )
    .single();

  if (error) throw error;
  return data;
}

/** Versions helpers unchanged */
export async function getNextVersionNo(article_id) {
  const { data, error } = await supabase
    .from("article_versions")
    .select("version_no")
    .eq("article_id", article_id)
    .order("version_no", { ascending: false })
    .limit(1);
  if (error) throw error;
  return data?.[0]?.version_no ? data[0].version_no + 1 : 1;
}

export async function createArticleVersion({
  article_id,
  title,
  content_html,
  meta_tags,
  score,
  readability_score,
  topic_authority_score,
  content_value_score,
  seo_score,
}) {
  const {
    data: { user },
    error: authErr,
  } = await supabase.auth.getUser();
  if (authErr) throw authErr;

  const version_no = await getNextVersionNo(article_id);

  const { data, error } = await supabase
    .from("article_versions")
    .insert({
      article_id,
      version_no,
      title,
      content_html,
      meta_tags,
      created_by: user.id,
      score: score ?? null,
      readability_score: readability_score ?? null,
      topic_authority_score: topic_authority_score ?? null,
      content_value_score: content_value_score ?? null,
      seo_score: seo_score ?? null,
    })
    .select("id, version_no")
    .single();
  if (error) throw error;
  return data;
}

/** The most recently created version for an article (its current "tip"). */
export async function getLatestArticleVersion(article_id) {
  const { data, error } = await supabase
    .from("article_versions")
    .select("id, version_no")
    .eq("article_id", article_id)
    .order("version_no", { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error) throw error;
  return data;
}

/**
 * Updates the score snapshot on an already-saved version in place — used
 * when Analyze runs again on a version that's already been saved, so its
 * scores reflect the analysis instead of staying null until the next save.
 */
export async function updateArticleVersionScores(versionId, scores) {
  const { error } = await supabase
    .from("article_versions")
    .update(scores)
    .eq("id", versionId);
  if (error) throw error;
}

export async function listArticleVersions(article_id) {
  const { data, error } = await supabase
    .from("article_versions")
    .select("id, version_no, title, created_at, created_by")
    .eq("article_id", article_id)
    .order("version_no", { ascending: false });
  if (error) throw error;
  return data || [];
}

export async function getArticleVersion(id) {
  const { data, error } = await supabase
    .from("article_versions")
    .select(
      "id, article_id, version_no, title, content_html, meta_tags, created_at, created_by, score, readability_score, topic_authority_score, content_value_score, seo_score"
    )
    .eq("id", id)
    .single();
  if (error) throw error;
  return data;
}

export async function getPreviousVersion(articleId, versionNo) {
  if (!versionNo || versionNo <= 1) return null;
  const { data, error } = await supabase
    .from("article_versions")
    .select("id, article_id, version_no, title, content_html, created_at")
    .eq("article_id", articleId)
    .lt("version_no", versionNo)
    .order("version_no", { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error) throw error;
  return data;
}

export async function deleteArticleVersion(versionId) {
  const { error } = await supabase
    .from("article_versions")
    .delete()
    .eq("id", versionId);
  if (error) throw error;
}

export async function deleteArticle(id) {
  const res = await fetch(`/api/articles/${id}`, {
    method: "DELETE",
    headers: await getAuthHeader(),
  });
  const json = await res.json();
  if (!res.ok) throw new Error(json?.error || "Failed to delete article");
  return true;
}

export async function renameArticle(id, title) {
  const res = await fetch(`/api/articles/${id}`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      ...(await getAuthHeader()),
    },
    body: JSON.stringify({ title }),
  });
  const json = await res.json();
  if (!res.ok) throw new Error(json?.error || "Failed to rename article");
  return json.article;
}
